<template>
  <q-page class="status-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="status-page__inner">
      <!-- Hero -->
      <header class="status-page__hero">
        <KdsEyebrow>{{ t('status.eyebrow') }}</KdsEyebrow>
        <h1 class="status-page__title">{{ t('status.title') }}</h1>
        <p class="status-page__subtitle">{{ t('status.subtitle') }}</p>
        <div class="status-page__global-badge">
          <span
            class="status-page__indicator"
            :class="`status-page__indicator--${globalStatus}`"
            aria-hidden="true"
          />
          <span class="status-page__badge-label">{{ t('status.allOperational') }}</span>
        </div>
      </header>

      <!-- Services -->
      <section class="status-page__section" :aria-label="t('status.services.title')" data-reveal>
        <h2 class="status-page__section-title">{{ t('status.services.title') }}</h2>
        <div class="status-page__cards">
          <StatusServiceCard
            :name="t('status.services.aiName')"
            :description="t('status.services.aiDescription')"
            :status="aiStatus.status"
            :loading="loading"
            :disclaimer="aiStatus.disclaimer"
          />
          <StatusServiceCard
            :name="t('status.services.deploymentName')"
            :description="t('status.services.deploymentDescription')"
            :status="deploymentStatus.status"
            :loading="loading"
          />
        </div>
      </section>

      <!-- Uptime -->
      <section class="status-page__section" data-reveal>
        <h2 class="status-page__section-title">{{ t('status.uptime.title') }}</h2>
        <p class="status-page__section-subtitle">{{ t('status.uptime.subtitle') }}</p>
        <div class="status-page__uptime-charts">
          <UptimePlaceholder
            :days="90"
            :status="aiStatus.status"
          />
          <UptimePlaceholder
            :days="90"
            :status="deploymentStatus.status"
          />
        </div>
        <div class="status-page__uptime-legend">
          <span class="status-page__uptime-legend-start">{{ t('status.uptime.legendStart') }}</span>
          <span class="status-page__uptime-legend-end">{{ t('status.uptime.legendEnd') }}</span>
        </div>
      </section>

      <!-- Incident history -->
      <section class="status-page__section">
        <h2 class="status-page__section-title">{{ t('status.incidents.title') }}</h2>
        <IncidentTimeline :incidents="[]" />
      </section>

      <!-- Footnote -->
      <p class="status-page__footnote">
        <template v-if="lastCheckedTime">
          {{ t('status.footnote.lastChecked', { time: lastCheckedTime }) }}
        </template>
        <template v-else>
          {{ t('status.footnote.refreshed') }}
        </template>
      </p>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import { KdsStatusServiceCard as StatusServiceCard } from '@/kds';
import UptimePlaceholder from '@/components/status/UptimePlaceholder.vue';
import IncidentTimeline from '@/components/status/IncidentTimeline.vue';
import { useSeoMeta } from '@/composables/useSeoMeta';
import { fetchStatusSnapshot } from '@/services/statusService';
import type { ServiceStatus, StatusKind } from '@/types/status';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';

const { t } = useI18n();
useSeoMeta('status');
const router = useRouter();
useRevealOnScroll();
const { openPreferences } = useConsent();

const loading = ref(true);
const lastCheckedTime = ref<string | null>(null);

const defaultAiStatus: ServiceStatus = {
  id: 'ai',
  name: 'AI infrastructure',
  description: 'Gemini-powered panel generation and debate engine.',
  status: 'operational',
  source: 'mock'
};

const defaultDeploymentStatus: ServiceStatus = {
  id: 'deployment',
  name: 'Deployment',
  description: 'Vercel edge deployment and CDN.',
  status: 'operational',
  source: 'mock'
};

const aiStatus = ref<ServiceStatus>(defaultAiStatus);
const deploymentStatus = ref<ServiceStatus>(defaultDeploymentStatus);

const globalStatus = computed<StatusKind>(() => {
  const statuses = [aiStatus.value.status, deploymentStatus.value.status];
  if (statuses.includes('outage')) return 'outage';
  if (statuses.includes('degraded')) return 'degraded';
  return 'operational';
});

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};

onMounted(async () => {
  try {
    const snapshot = await fetchStatusSnapshot();

    for (const service of snapshot.services) {
      if (service.id === 'ai') {
        aiStatus.value = service;
      } else if (service.id === 'deployment') {
        deploymentStatus.value = service;
        if (service.lastChecked) {
          const date = new Date(service.lastChecked);
          lastCheckedTime.value = date.toISOString().substring(11, 16);
        }
      }
    }
  } catch {
    // Fallback mock already in place via defaultAiStatus and defaultDeploymentStatus
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
.status-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 800px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
    display: flex;
    flex-direction: column;
    gap: var(--space-10);
  }

  &__hero {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__subtitle {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--stone);
  }

  &__global-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    border: var(--border);
    border-radius: var(--r-pill);
    width: fit-content;
  }

  &__indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &--operational {
      background: var(--ok);
    }

    &--degraded {
      background: var(--warn);
    }

    &--outage {
      background: var(--danger);
    }
  }

  &__badge-label {
    font-size: var(--fs-body-sm);
    font-weight: var(--fw-medium);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink);
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h3);
    line-height: 1.22;
    letter-spacing: -0.01em;
    color: var(--ink);
    text-transform: uppercase;
  }

  &__section-subtitle {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--stone);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__cards {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__uptime-charts {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__uptime-legend {
    display: flex;
    justify-content: space-between;
    font-size: var(--fs-meta);
    color: var(--stone);
  }

  &__uptime-legend-start,
  &__uptime-legend-end {
    // inline
  }

  &__footnote {
    margin: 0;
    font-size: var(--fs-meta);
    color: var(--stone);
    text-align: center;
  }
}
</style>
